import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import { ProductGallery, type GalleryShot } from '../components/ProductGallery';
import { formatPrice } from '../data/products';
import { useProduct } from '../lib/catalog';
import { useCart } from '../lib/cart';
import { productGalleryShots, productHeroShot } from '../lib/productMedia';

export function ProductPage() {
  const { slug } = useParams();
  const { product, loading, error, notFound } = useProduct(slug);
  const { addItem } = useCart();
  const [heroZoom, setHeroZoom] = useState(false);

  if (loading) {
    return (
      <div className="container" style={{ padding: '4rem 0' }}>
        <p className="muted">Loading extension…</p>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="container" style={{ padding: '4rem 0' }}>
        <div className="card checkout-panel">
          <h1>Product not found</h1>
          <p className="muted">{error || 'That extension is not on the published roster.'}</p>
          <Link className="btn btn-primary" to="/marketplace">
            Back to Marketplace
          </Link>
        </div>
      </div>
    );
  }

  const hero = productHeroShot(product);
  const galleryShots = productGalleryShots(product);
  const heroShot: GalleryShot | null = hero
    ? { src: hero.src, alt: hero.alt, caption: 'Hero image' }
    : null;

  return (
    <div className="container" style={{ paddingTop: '2rem' }}>
      <p style={{ marginBottom: '1rem' }}>
        <Link to="/marketplace" className="muted">
          ← Bizi Marketplace
        </Link>
      </p>

      <div className="product-detail">
        <div className="card detail-main">
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {product.badge && <span className="badge">{product.badge}</span>}
            <span className="badge">{product.category}</span>
          </div>
          <h1>{product.name}</h1>
          <p className="muted" style={{ fontSize: '1.05rem', marginBottom: '1rem' }}>
            {product.tagline}
          </p>
          <p style={{ color: 'var(--text-muted)' }}>{product.description}</p>

          {heroShot && (
            <figure
              className="product-hero product-hero-zoomable"
              onClick={() => setHeroZoom(true)}
              role="button"
              tabIndex={0}
              aria-label="Click to view hero image full size"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setHeroZoom(true);
                }
              }}
            >
              <img src={heroShot.src} alt={heroShot.alt} />
              <span className="product-hero-zoom-hint" aria-hidden="true">
                <span className="material-symbols-outlined">zoom_in</span>
              </span>
            </figure>
          )}

          {heroZoom && heroShot && (
            <HeroLightbox shot={heroShot} onClose={() => setHeroZoom(false)} />
          )}

          <ProductGallery screenshots={galleryShots} productName={product.name} />

          {product.features.length > 0 && (
            <>
              <h3 style={{ marginTop: '1.75rem', marginBottom: '0.5rem' }}>Features</h3>
              <ul className="feature-list">
                {product.features.map((f) => (
                  <li key={f}>
                    <span className="material-symbols-outlined">check_circle</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="compat-box">
            <h3>Compatibility</h3>
            <dl>
              <dt>EspoCRM</dt>
              <dd>{product.compatibility.espocrm || '—'}</dd>
              <dt>PHP</dt>
              <dd>{product.compatibility.php || '—'}</dd>
              <dt>Version</dt>
              <dd>{product.version ? `v${product.version}` : '—'}</dd>
              {product.compatibility.notes && (
                <>
                  <dt>Notes</dt>
                  <dd>{product.compatibility.notes}</dd>
                </>
              )}
            </dl>
          </div>
        </div>

        <aside className="card detail-buy">
          <div className="eyebrow" style={{ marginBottom: '0.35rem' }}>
            Digital license
          </div>
          <div className="price">{formatPrice(product.priceCents, product.currency)}</div>
          <p className="buy-note">One-time purchase · Instant download after payment · Receipt by email</p>
          <div className="buy-stack">
            <button
              className="btn btn-primary btn-lg"
              onClick={() => addItem(product)}
              disabled={!product.hasPackage}
            >
              {product.hasPackage ? 'Purchase with Stripe™' : 'Package uploading soon'}
            </button>
            <Link className="btn btn-ghost" to="/marketplace">
              Keep browsing
            </Link>
          </div>
          <p className="muted" style={{ marginTop: '1rem', fontSize: '0.85rem' }}>
            Adds this extension to your session cart. Checkout is processed securely by Stripe™.
          </p>
        </aside>
      </div>
    </div>
  );
}

/**
 * Simple single-image lightbox rendered via portal to document.body.
 * Same styling as the gallery FullScreenLightbox but for a single image.
 */
function HeroLightbox({ shot, onClose }: { shot: GalleryShot; onClose: () => void }) {
  return createPortal(
    <div className="fs-lightbox" role="dialog" aria-modal="true" aria-label="Full size image">
      <div className="fs-lightbox-backdrop" onClick={onClose} />
      <div className="fs-lightbox-content">
        <div className="fs-lightbox-header">
          <span className="fs-lightbox-counter">{shot.caption || 'Image'}</span>
          <button
            type="button"
            className="fs-lightbox-close"
            onClick={onClose}
            aria-label="Close full size image"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <div className="fs-lightbox-img-wrap" onClick={onClose}>
          <img
            className="fs-lightbox-img"
            src={shot.src}
            alt={shot.alt}
            draggable={false}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
